import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import SearchableSelect from '@/components/ui/SearchableSelect';

/**
 * Add Customer modal — mirrors the form on the Customers page exactly
 * (same fields, same validation, same divisions dropdown). Designed to be
 * embedded from forms like Add Lead / Add Invoice so users can create a new
 * customer without leaving their current screen.
 *
 * On successful POST /customers the freshly-created record is bubbled up via
 * `onCreated(customer)` so the parent form can immediately select it.
 */
const QuickAddCustomerModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    contact_person: '',
    vat_reg_no: '',
    cr_no: '',
    region: '',
    business_vertical: '',
    division: '',
    credit_period_days: 30,
    address: '',
  });
  const [divisions, setDivisions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/master-data/divisions');
        setDivisions(res.data || []);
      } catch {
        /* silent — fallback to empty list */
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!formData.name.trim()) {
      setError('Customer name is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      // Strip empty optional fields so backend's EmailStr / typed validators
      // don't reject empty strings (e.g. `email: ''`).
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([k, v]) => {
          if (k === 'credit_period_days') return v !== '' && v != null;
          return v !== '' && v != null;
        })
      );
      const res = await api.post('/customers', payload);
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create customer.');
    } finally {
      setBusy(false);
    }
  };

  // Reuse the same mobile-modal CSS classes as Customers.js for visual parity.
  return (
    <div className="mobile-modal" data-testid="create-customer-modal">
      <div className="mobile-modal-content">
        <div className="mobile-modal-header">
          <h2 className="text-xl sm:text-2xl font-bold">Add New Customer</h2>
        </div>
        <form onSubmit={handleSubmit} className="mobile-modal-body" data-testid="create-customer-form">
          {error && (
            <div
              className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2 mb-3"
              data-testid="quick-add-customer-error"
            >
              {error}
            </div>
          )}
          <div className="mobile-form">
            <div>
              <label className="block text-sm mb-1 font-medium">Name *</label>
              <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mobile-input" data-testid="customer-name-input" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Email *</label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mobile-input" data-testid="customer-email-input" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Phone</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="mobile-input" data-testid="customer-phone-input" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Contact Person</label>
              <input type="text" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="mobile-input" data-testid="customer-contact-input" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">VAT REG NO.</label>
              <input type="text" value={formData.vat_reg_no} onChange={(e) => setFormData({ ...formData, vat_reg_no: e.target.value })} className="mobile-input" data-testid="customer-vat-input" placeholder="e.g., BH123456789" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">CR NO.</label>
              <input type="text" value={formData.cr_no} onChange={(e) => setFormData({ ...formData, cr_no: e.target.value })} className="mobile-input" data-testid="customer-cr-input" placeholder="e.g., CR-12345" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Region</label>
              <input type="text" value={formData.region} onChange={(e) => setFormData({ ...formData, region: e.target.value })} className="mobile-input" data-testid="customer-region-input" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Business Vertical</label>
              <input type="text" value={formData.business_vertical} onChange={(e) => setFormData({ ...formData, business_vertical: e.target.value })} className="mobile-input" data-testid="customer-business-input" />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Customer Division</label>
              <SearchableSelect
                value={formData.division}
                onChange={(val) => setFormData({ ...formData, division: val })}
                options={divisions.map((div) => ({ value: div.name, label: div.name }))}
                placeholder="Select Division"
              />
            </div>
            <div>
              <label className="block text-sm mb-1 font-medium">Credit Period (days)</label>
              <input
                type="number"
                min="0"
                max="365"
                value={formData.credit_period_days ?? 30}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credit_period_days:
                      e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0,
                  })
                }
                className="mobile-input"
                data-testid="customer-credit-period-input"
                placeholder="30"
              />
              <p className="text-xs text-gray-500 mt-1">Used to calculate invoice due dates</p>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm mb-1 font-medium">Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="mobile-input"
              rows="2"
              data-testid="customer-address-input"
            />
          </div>
        </form>
        <div className="mobile-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="mobile-btn border border-gray-300 w-full sm:w-auto"
            data-testid="cancel-create-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            className="mobile-btn bg-gradient-to-r from-blue-700 to-green-700 text-white w-full sm:w-auto disabled:opacity-60"
            data-testid="submit-create-customer-btn"
          >
            {busy ? 'Creating…' : 'Create Customer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickAddCustomerModal;
